// src/components/frontpage/MobileFrontPage.jsx
// Mobile locality front page — five full-width screens (Directory / News / Chat / Events / Groups).
// Switched by mobileScreen state driven from the header mode bar.
// Renders column components directly (not via MultiViewGrid) for full-width control.
// Directory mounts the shared DirectoryColumn (same component + useDirectoryListings hook
// as desktop), replacing the retired MobileSidebarPanel sliding drawer.
// Marketplace (mock/session-local swap state) was retired from this tab bar in favour of
// Groups (July 2026) — see HeaderButtonsFeed.jsx. SwapColumn/SwapCreateModal/SwapDetailModal
// and mockFrontPage.getSwapListings remain in the tree, unused here, for when it returns.

import React, { useState, useEffect } from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import NewsGrid from '../news/NewsGrid';
import ChatColumn from './columns/ChatColumn';
import EventsColumn from './columns/EventsColumn';
import GroupsColumn from './columns/GroupsColumn';
import DirectoryColumn from './columns/DirectoryColumn';
import InstallAppPrompt from '../core/InstallAppPrompt';
import MobileActionLine from '../core/HeadersAndFooters/MobileActionLine';
import { supabaseFetch } from '../../lib/supabase';
import { parseEventDate } from '../../utils/eventDateTime';
import { tokens } from '../../styles/designTokens';

const MobileFrontPage = ({
  articles = [],
  chats = [],
  events = [],
  onArticleClick,
  onChatClick,
  onEventClick,
  mobileScreen = 'news',
  // Chat accordion props
  openChatThreadId = null,
  onChatThreadToggle,
  selectedArea,
  onChatsRefresh,
  // Directory tab props
  userHomeLocationName,
  userHomeLocationCoords,
  currentUser,
  sessionReady = false,
  onGroupSelect,
  guestFallbackBoundaryId = null,
  visitBoundaryId = null,
  // Load More for News and Events (Roadmap #104); null when not offered
  feedPaging = null
}) => {
  const theme = useTheme();
  const { t } = useTranslation('frontpage');
  const { requireAuth } = useAuth();
  // Ambient ThemeProvider colour — locality blue throughout this five-tab composite (it never
  // switches to Groups-mauve; that only happens once an actual group is entered). Used for the
  // Directory/News sub-headers, which are never shown on the Groups tab so never need the accent.
  const contextColor = theme?.primary || tokens.colors.contextLocality;

  // Fixed tab order — must match HeaderButtonsFeed.jsx's MOBILE_SCREENS exactly, since the
  // notch below is positioned purely by index/count, with no shared DOM/state connecting this
  // normal-flow content to the fixed-position tab bar above it.
  const SCREEN_ORDER = ['directory', 'news', 'chat', 'events', 'groups'];
  const activeTabIndex = Math.max(0, SCREEN_ORDER.indexOf(mobileScreen));

  // The viewport frame's own accent — mauve specifically for the Groups tab (matching
  // HeaderButtonsFeed's tabAccentColor and GroupsColumn's own deliberate mauve sourcing),
  // ambient blue otherwise. Addendum 3 fault 3: this frame previously always used the ambient
  // `contextColor`, so it stayed locality-blue even while the active tab and its sub-header
  // were correctly mauve on the Groups tab.
  const viewportAccentColor = mobileScreen === 'groups' ? tokens.colors.contextGroups : contextColor;

  // Notched top border: one continuous 2px line in viewportAccentColor across the frame's full
  // width, EXCEPT the segment directly under the active tab (addendum 3 fault 1 — no line where
  // the active tab meets what's below it). A real `border` can't have a gap, so this is a
  // background gradient painted into a matching padding-top strip instead (see the frame's own
  // style below). Segment maths: five equal tabs, so each is exactly 1/5 of the width.
  const notchStart = (activeTabIndex / SCREEN_ORDER.length) * 100;
  const notchEnd = ((activeTabIndex + 1) / SCREEN_ORDER.length) * 100;
  const frameTopNotch = `linear-gradient(to right, ${viewportAccentColor} 0%, ${viewportAccentColor} ${notchStart}%, transparent ${notchStart}%, transparent ${notchEnd}%, ${viewportAccentColor} ${notchEnd}%, ${viewportAccentColor} 100%)`;

  // Directory's sub-header (search only — its create affordance is the Suggest CTA already in
  // ModeColumn's own pinned footer slot, unmoved) is composed HERE, outside DirectoryColumn's
  // own 12px horizontally-padded wrapper below — rendering it inside that padding made its
  // search box narrower than the other four tabs' (addendum 1, defect 1).
  const directorySubHeader = (
    <MobileActionLine
      contextColor={contextColor}
      topBorder={false}
      sideBorders={false}
      searchPlaceholder={t('ui:search_articles')}
      onSearch={(term) => window.dispatchEvent(new CustomEvent('ournet:search-articles', { detail: { searchTerm: term } }))}
    />
  );

  // News sub-header create — the one screen (besides Directory, above) this file composes
  // directly (NewsGrid isn't column-shaped/ModeColumn-wrapped, and is reused elsewhere where
  // front-page create/search logic doesn't belong, so it isn't given its own hideColumnHeader-
  // style prop). Retires the old shared HeaderButtonsFeed Row 3 for this tab (mobile-tab-
  // subheaders brief). Mirrors the per-panel handlers already living inside Chat/Events/Groups.
  const handleNewsCreate = () => {
    requireAuth('gate_body_generic', () => {
      window.dispatchEvent(new CustomEvent('ournet:navigate', { detail: { view: 'walking-composer' } }));
    });
  };
  // Article creation is not available while visiting (Change 3, addendum-1 §A/§C) — hidden.
  const isVisiting = !!visitBoundaryId;
  const newsSubHeader = (
    <MobileActionLine
      contextColor={contextColor}
      createLabel={t('mobile_create_walking')}
      onCreate={isVisiting ? undefined : handleNewsCreate}
      topBorder={false}
      sideBorders={false}
      searchPlaceholder={t('ui:search_articles')}
      onSearch={(term) => window.dispatchEvent(new CustomEvent('ournet:search-articles', { detail: { searchTerm: term } }))}
    />
  );

  // Event details (start_date, venue_name etc.)
  const [eventDetails, setEventDetails] = useState({});

  // Fetch event_details for real events
  useEffect(() => {
    if (!events || events.length === 0) {
      setEventDetails({});
      return;
    }

    let cancelled = false;
    const fetchEventDetails = async () => {
      const eventIds = events.map(e => e.id).filter(Boolean);
      if (eventIds.length === 0) return;

      const { data, error } = await supabaseFetch.query('event_details', {
        select: 'article_id,start_date,end_date,display_from,venue_name,venue_address',
        inFilter: { article_id: eventIds }
      });

      if (error) {
        console.error('Error fetching event_details:', error);
        return;
      }

      if (!cancelled && data) {
        const detailsMap = {};
        for (const d of data) {
          detailsMap[d.article_id] = d;
        }
        setEventDetails(detailsMap);
      }
    };

    fetchEventDetails();
    return () => { cancelled = true; };
  }, [events]);

  // --- Chat data: filter to originals, sorted by most recent activity ---
  const chatData = (chats || [])
    .filter(c => !c.parent_chat_id)
    .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));

  // --- Event data: merge event_details, filter to the display window, sort soonest first ---
  const eventData = (events || [])
    .filter(e => e.article_type === 'event')
    .map(e => {
      const details = eventDetails[e.id];
      return {
        ...e,
        start_date: details?.start_date || null,
        end_date: details?.end_date || null,
        display_from: details?.display_from || null,
        venue_name: details?.venue_name || null,
        venue_address: details?.venue_address || null
      };
    })
    // Full roster — the Events column owns the Future/Past split (no feed-window pre-filter here).
    .sort((a, b) => parseEventDate(a.start_date) - parseEventDate(b.start_date));

  // News articles (admit 'website' as news-family for display; locality feeds never carry
  // website embeds — those are group-scoped by community_id)
  const newsArticles = (articles || []).filter(a => a.article_type === 'news' || a.article_type === 'website');

  const renderScreen = () => {
    switch (mobileScreen) {
      case 'directory':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            {directorySubHeader}
            {/* Slim horizontal padding so directory content doesn't touch the screen edges
                (desktop relies on the grid column gap instead). Install sits at the body
                bottom. Padding starts BELOW the sub-header, not around it (addendum 1,
                defect 1) — the sub-header itself stays edge-to-edge like the other four tabs'. */}
            <div style={{ flex: 1, minHeight: 0, padding: '0 12px', boxSizing: 'border-box' }}>
              <DirectoryColumn
                selectedArea={selectedArea}
                currentUser={currentUser}
                guestFallbackBoundaryId={guestFallbackBoundaryId}
                visitBoundaryId={visitBoundaryId}
                userHomeLocationName={userHomeLocationName}
                userHomeLocationCoords={userHomeLocationCoords}
                hideColumnHeader={true}
                bodyFooter={<InstallAppPrompt />}
              />
            </div>
          </div>
        );

      case 'news':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            {newsSubHeader}
            {/* flex:1/minHeight:0 gives NewsGrid a definite height to resolve its own internal
                height:'100%' against — NewsGrid already manages its own overflowY:'auto' scroll,
                so this wrapper doesn't add a second scroll region. */}
            <div style={{ flex: 1, minHeight: 0 }}>
              <NewsGrid
                articles={newsArticles}
                onArticleClick={onArticleClick}
                currentContext={{ type: 'location' }}
                isMobile={true}
                paging={feedPaging?.news}
                pagingBusy={Boolean(feedPaging?.busy)}
              />
            </div>
          </div>
        );

      case 'chat':
        return (
          <div style={{ height: '100%', overflow: 'hidden' }}>
            <ChatColumn
              data={chatData}
              onClick={onChatClick}
              openChatThreadId={openChatThreadId}
              onChatThreadToggle={onChatThreadToggle}
              selectedArea={selectedArea}
              onChatsRefresh={onChatsRefresh}
              visitBoundaryId={visitBoundaryId}
              hideColumnHeader={true}
            />
          </div>
        );

      case 'events':
        return (
          <EventsColumn
            data={eventData}
            onClick={onEventClick}
            hideColumnHeader={true}
            visitBoundaryId={visitBoundaryId}
            paging={feedPaging}
          />
        );

      case 'groups':
        return (
          <GroupsColumn
            currentUser={currentUser}
            sessionReady={sessionReady}
            onGroupSelect={onGroupSelect}
            hideColumnHeader={true}
            selectedArea={selectedArea}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
      overflow: 'hidden',
      boxSizing: 'border-box',
      // The single frame enclosing tab + sub-header + viewport as one shape (mobile-tab-
      // subheaders addendum 3) — sides + bottom are an ordinary border; each panel's own
      // sub-header now contributes no border of its own (MobileActionLine's topBorder/
      // sideBorders={false}), so this is the ONLY L/R/bottom border in the whole region,
      // avoiding the doubled width the two separately-bordered containers produced (fault 2).
      // The top can't be an ordinary border — it needs a gap under the active tab (fault 1) —
      // so it's a background gradient (frameTopNotch) painted into a matching 2px padding
      // strip instead; genuinely open there, not just colour-matched.
      borderLeft: `2px solid ${viewportAccentColor}`,
      borderRight: `2px solid ${viewportAccentColor}`,
      borderBottom: `2px solid ${viewportAccentColor}`,
      paddingTop: '2px',
      backgroundImage: frameTopNotch,
      backgroundRepeat: 'no-repeat',
      backgroundSize: '100% 2px',
      backgroundPosition: 'top left',
      // background-origin defaults to padding-box, which would resolve the gradient's 0%/100%
      // against a box already inset ~2px by the L/R borders themselves — off by sub-pixel
      // amounts from the tab bar's own division of the TRUE, un-inset viewport width (a
      // separate fixed-position element, no shared coordinate system). border-box makes the
      // gradient's 0%/100% match the frame's own declared width exactly, same as the tab bar's.
      backgroundOrigin: 'border-box',
      backgroundClip: 'border-box'
    }}>
      {/* Active screen — full-width, full remaining height */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {renderScreen()}
      </div>
    </div>
  );
};

export default MobileFrontPage;
